import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import { ErrorState } from '../components/shared/StateMessage';
import PanelErrorBoundary, { sanitizePanelError } from '../components/shared/PanelErrorBoundary';
import { useView } from '../contexts/ViewContext';
import { getSearchConsole, getSearchConsoleDates, drill } from '../api';

import PanelCard from '../components/search-console/PanelCard';
import GenericCard from '../components/search-console/GenericCard';
import VerdictHeader from '../components/search-console/VerdictHeader';
import WorthKnowing from '../components/search-console/WorthKnowing';
import SearchConsoleSkeleton from '../components/search-console/SearchConsoleSkeleton';
import HistoricalBanner from '../components/search-console/HistoricalBanner';
import AsOfPicker from '../components/search-console/AsOfPicker';
import DetailRangePicker from '../components/search-console/DetailRangePicker';
import DrillOver from '../components/shared/DrillOver';
import evidenceFor, { hasDepth, sentenceOnly } from '../components/search-console/evidenceFor';
import { stripModelNotes } from '../components/search-console/lib/modelNotes';
import { readExpanded, writeExpanded, toggleExpanded } from '../components/search-console/lib/expansion';
import { windowLabel, fmtSearchTerm } from '../components/search-console/lib/format';
import { titleCase } from '../components/shared/format';

const NO_DATA_COPY = 'Search Console data is still being set up for this property.';

/**
 * Search Console (V6).
 *
 * A RENDER-ONLY surface. Every number is pre-computed and every sentence pre-written
 * by a nightly job; this page formats, lays out and computes NOTHING (§I). A wrong
 * number is a backend bug to flag, never to patch.
 *
 * V6 is a COMPOSED LAYOUT, not a ranked stream (spec §2.4.0a): the shell places
 * panels by key into designed tier regions and orders within a region by rank. It
 * never reorders against rank and never drops a panel because a region had no slot;
 * an absent panel collapses its slot with no placeholder. Each tier is wrapped in a
 * `PanelErrorBoundary` so one malformed panel cannot blank the page.
 *
 * The one face is the results ladder (Tier 3). Every drillable number opens the one
 * shared `DrillOver` (§4.10); the drill never navigates away, so an owner who set an
 * as-of date keeps it.
 *
 * Data: one `Promise.all` on load (`getSearchConsole` + `getSearchConsoleDates`).
 * No panel fetches its own data on load; table page-changes and drills are their own
 * fetches. The only two refetch triggers are a property change and an as-of change
 * (§12).
 */

// ── Tier region maps ──────────────────────────────────────────────────────────
// Each region is a fixed layout: which panel keys sit where, and the geometry of
// paired slots. Within a region the spec's order already follows the backend rank,
// so placing by key preserves rank order without an explicit sort. Absent keys
// collapse (see `renderSlot` / `renderPair`).
// `nearly_there` and `new_queries` are the row-heavy worklists — they render full width
// (see the Tier-4 render) rather than in a pair, so their long lists get the room.
const TIER4_PAIRS = [
  { left: 'movers', right: 'brand_split', ratio: '38/62' },
  { left: 'discovery_breadth', right: 'demand_season', ratio: '38/62' },
];

const RATIO_COLS = {
  '62/38': 'lg:grid-cols-[3fr_2fr]',
  '38/62': 'lg:grid-cols-[2fr_3fr]',
  '50/50': 'lg:grid-cols-2',
};

// Every panel key the tiers place into a designed slot. Any panel NOT in this set —
// an unknown key the backend adds on its own cadence (§2.4.9) — is rendered in a tail
// after Tier 5 rather than dropped, so the shell never loses a panel.
const PLACED_KEYS = new Set([
  'search_headline', 'position_bands',
  'nearly_there', 'new_queries', 'movers', 'brand_split', 'discovery_breadth', 'demand_season',
  'top_queries', 'image_search', 'device_split', 'top_pages', 'query_page_map',
  'indexing_health', 'data_coverage', 'sitemap_health', 'discover_traffic', 'search_appearance',
]);

const SearchConsolePage = () => {
  usePageMeta({ headerActive: 'Search Console', sidebarActive: 'search-console' });
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const isClient = view.isClient;
  // §10: the as-of picker is admin only — an audit control, not something an owner
  // header should carry. `p_as_of` keeps working for everyone; only the CONTROL is gated.
  const isAdmin = view.isAdmin;

  const clientId = view.supabaseClientId;
  const clientDisplayName = view.clientDisplayName;

  // ── URL state: panel deep link only ─────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const panelParam = searchParams.get('panel');

  // ── Data ────────────────────────────────────────────────────────────────────
  // The chosen snapshot is stored WITH the property it belongs to and read back only
  // while the two still match — resetting it in an effect would fire the page RPC
  // twice on a property switch.
  const [asOfState, setAsOfState] = useState({ clientId: null, date: null });
  const asOf = asOfState.clientId === clientId ? asOfState.date : null;
  const setAsOf = useCallback((date) => setAsOfState({ clientId, date }), [clientId]);
  const [intel, setIntel] = useState({ loading: false, error: '', data: null });
  const [dates, setDates] = useState({ loading: false, error: '', data: null });
  const [detailRange, setDetailRange] = useState({ from: null, to: null });
  const [compareRange, setCompareRange] = useState(null);

  // ── Expansion (§3) ──────────────────────────────────────────────────────────
  const [expanded, setExpanded] = useState(() => readExpanded(username));
  const onToggle = useCallback((panelKey) => {
    setExpanded((prev) => {
      const next = toggleExpanded(prev, panelKey);
      writeExpanded(username, next);
      return next;
    });
  }, [username]);

  // ── Drill stack (§4.10) ──────────────────────────────────────────────────────
  // One shared DrillOver for every drill on the page. A frame is `{ kind, label,
  // rows }`; a row carrying its own `drill` pushes the next frame. A loading
  // placeholder is pushed immediately so the overlay opens without a blank beat, and
  // is replaced when `fn_drill` resolves.
  //
  // The backend currently echoes the machine `what` back as `label` (e.g. "gsc.new_query"),
  // which is not a title to show a customer. When that happens, title the frame with the
  // thing that was clicked (the drill's `id` — a search term, page path or device) and
  // fall back to a readable name for the drill kind.
  const DRILL_KIND_LABELS = useMemo(() => ({
    'gsc.query': 'Search', 'gsc.new_query': 'New search', 'gsc.mover': 'Search',
    'gsc.page': 'Page', 'gsc.inspected_url': 'Page', 'gsc.device': 'Device',
    'gsc.position_band': 'Position range', 'gsc.brand_month': 'Month', 'gsc.sitemap': 'Sitemap',
  }), []);
  const resolveDrillLabel = useCallback((rawLabel, drillObj) => {
    const machine = !rawLabel || rawLabel === drillObj.value || /^gsc\./.test(rawLabel);
    if (!machine) return rawLabel;
    const id = drillObj.id != null ? String(drillObj.id).trim() : '';
    if (!id) return DRILL_KIND_LABELS[drillObj.value] || 'Details';
    // Present the clicked value the way it reads on the page: search terms title-cased
    // ("luxury rv resorts" → "Luxury RV Resorts"), Google's device enum title-cased
    // ("MOBILE" → "Mobile"); page paths and other ids stay verbatim.
    if (['gsc.query', 'gsc.new_query', 'gsc.mover'].includes(drillObj.value)) return fmtSearchTerm(id);
    if (drillObj.value === 'gsc.device') return titleCase(id);
    return id;
  }, [DRILL_KIND_LABELS]);
  const [drillStack, setDrillStack] = useState([]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setDates({ loading: true, error: '', data: null });
    getSearchConsoleDates(clientId)
      .then((data) => {
        if (cancelled) return;
        setDates({ loading: false, error: '', data: data ?? null });
      })
      .catch((e) => {
        if (!cancelled) setDates({ loading: false, error: e?.message || 'Failed to load dates', data: null });
      });
    return () => { cancelled = true; };
  }, [clientId]);

  const loadIntel = useCallback(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setIntel({ loading: true, error: '', data: null });
    getSearchConsole(clientId, asOf)
      .then((data) => {
        if (!cancelled) setIntel({ loading: false, error: '', data: data ?? null });
      })
      .catch((e) => {
        // §11: show an error state with a retry, and log it. The raw message is the
        // logged half; the rendered half is sanitised below.
        // eslint-disable-next-line no-console
        console.error('[search-console] page RPC failed', e);
        if (!cancelled) setIntel({ loading: false, error: e?.message || 'Failed to load Search Console', data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, asOf]);

  // Property change and as-of change — the only two refetch triggers (§12).
  useEffect(() => loadIntel(), [loadIntel]);

  // Detail range defaults to the panel-payload window, clamped into what the detail
  // tables can serve (`detail_from`/`detail_to`, §A.9) — waits for BOTH the dates RPC and
  // the page payload. This is a Detail-table control only; the page window is fixed at 90
  // days and the verdict/KPIs/panels are the nightly snapshot, unaffected by the range.
  const payloadLoaded = Boolean(intel.data);
  const payloadWindow = intel.data?.window;
  const detailFrom = dates.data?.detail_from;
  const detailTo = dates.data?.detail_to;
  const datesLoading = dates.loading;
  useEffect(() => {
    if (!payloadLoaded) return;
    if (detailFrom && detailTo) {
      const from = payloadWindow?.start && payloadWindow.start > detailFrom ? payloadWindow.start : detailFrom;
      const to = payloadWindow?.end && payloadWindow.end < detailTo ? payloadWindow.end : detailTo;
      setDetailRange({ from, to });
    } else if (!datesLoading && payloadWindow?.start && payloadWindow?.end) {
      // The dates RPC has SETTLED (resolved or rejected) without usable bounds — fall
      // back to the payload window rather than leaving a null range to flow into the
      // detail tables' paging. Gated on `!datesLoading` so this fires once, after the
      // dates call has had its chance, not as an interim that would read to DetailTable
      // like the reader moving the picker (and fire a live fetch on first render).
      setDetailRange({ from: payloadWindow.start, to: payloadWindow.end });
    }
  }, [detailFrom, detailTo, payloadLoaded, payloadWindow?.start, payloadWindow?.end, datesLoading]);

  // A property switch drops the comparison range and closes any open drill — both
  // belong to the previous property.
  const prevClientId = useRef(clientId);
  useEffect(() => {
    const prev = prevClientId.current;
    prevClientId.current = clientId;
    if (prev && clientId && prev !== clientId) {
      setCompareRange(null);
      setDrillStack([]);
    }
  }, [clientId]);

  const payload = intel.data;
  const panels = useMemo(() => (Array.isArray(payload?.panels) ? payload.panels : []), [payload]);
  const byKey = useMemo(() => {
    const m = {};
    panels.forEach((p) => { if (p?.panel) m[p.panel] = p; });
    return m;
  }, [panels]);
  const notable = useMemo(() => panels.filter((p) => p.emphasis === 'notable'), [panels]);

  // The drill scope carries the page's live filters, so a drill never loses the
  // reader's window (§4.10). `search_type` is web for this page.
  const drillScope = useMemo(() => ({
    client_id: clientId,
    start_date: payloadWindow?.start,
    end_date: payloadWindow?.end,
    search_type: 'web',
  }), [clientId, payloadWindow?.start, payloadWindow?.end]);

  const pushDrill = useCallback((drillObj) => {
    if (!drillObj?.value) return;
    const placeholder = { kind: drillObj.value, label: 'Loading…', rows: [], loading: true };
    setDrillStack((s) => [...s, placeholder]);
    drill(drillObj.value, drillObj.id, drillScope)
      .then((frame) => {
        const resolved = frame && typeof frame === 'object'
          ? { kind: frame.kind || drillObj.value, label: resolveDrillLabel(frame.label, drillObj), rows: Array.isArray(frame.rows) ? frame.rows : [] }
          : { kind: drillObj.value, label: resolveDrillLabel(null, drillObj), rows: [] };
        setDrillStack((s) => [...s.slice(0, -1), resolved]);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[search-console] drill RPC failed', e);
        setDrillStack((s) => [...s.slice(0, -1), {
          kind: drillObj.value,
          label: 'Could not load these records',
          rows: [],
          error: true,
        }]);
      });
  }, [drillScope, resolveDrillLabel]);

  // A Key Finding names a whole notable panel, which the backend gives no single drill
  // for — so opening one shows that panel's OWN rows as a drill frame (each row keeps
  // its real drill for a deeper descent), rather than a fabricated fn_drill call.
  const pushFrame = useCallback((frame) => {
    if (!frame || !Array.isArray(frame.rows) || frame.rows.length === 0) return;
    setDrillStack((s) => [...s, {
      kind: frame.kind || 'finding',
      label: frame.label || 'Details',
      subtitle: frame.subtitle,
      rows: frame.rows,
    }]);
  }, []);

  const popDrill = useCallback(() => setDrillStack((s) => s.slice(0, -1)), []);
  const closeDrill = useCallback(() => setDrillStack([]), []);
  const onRowDrill = useCallback((row) => pushDrill(row?.drill), [pushDrill]);

  // ── Deep link to a panel ────────────────────────────────────────────────────
  useEffect(() => {
    if (!panelParam || panels.length === 0) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(`panel-${panelParam}`);
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [panelParam, panels]);

  // Render one panel by its object. Insufficient confidence and unknown keys render
  // GenericCard (title + sentence, no evidence, no reserved space — §G.2 / §2.4.9).
  const renderPanel = useCallback((panel) => {
    if (!panel) return null;
    const Evidence = evidenceFor(panel.panel);
    // Model instructions are stripped before `facts` reaches a component (§2.4.8).
    const facts = stripModelNotes(panel.facts);
    if (!Evidence || panel.confidence === 'insufficient' || sentenceOnly(panel.panel, facts)) {
      // The backend sentence explains why there is nothing to draw; the empty-state
      // mark under it only repeated that. Keep the mark only when there is no sentence.
      return <GenericCard key={panel.panel} panel={panel} showEmptyMark={!panel.sentence} />;
    }
    const isExpanded = expanded.has(panel.panel);
    const expandable = hasDepth(panel.panel, facts);
    const isOpen = isExpanded && expandable;
    return (
      <PanelCard
        key={panel.panel}
        panel={panel}
        expanded={isExpanded && expandable}
        onToggle={onToggle}
        expandable={expandable}
      >
        <Evidence
          panel={panel.panel}
          facts={facts}
          sentence={panel.sentence}
          clientId={clientId}
          detailRange={detailRange}
          compareRange={compareRange}
          collapsed={!isOpen}
          onExpand={expandable && !isOpen ? () => onToggle(panel.panel) : undefined}
          onDrill={pushDrill}
          scope={drillScope}
        />
      </PanelCard>
    );
  }, [expanded, onToggle, clientId, detailRange, compareRange, pushDrill, drillScope]);

  const renderSlot = useCallback((key) => {
    const p = byKey[key];
    return p ? renderPanel(p) : null;
  }, [byKey, renderPanel]);

  // A paired region: both present → asymmetric grid; one present → it takes the full
  // width; neither → nothing (the slot collapses).
  const renderPair = useCallback(({ left, right, ratio }) => {
    const L = renderSlot(left);
    const R = renderSlot(right);
    if (!L && !R) return null;
    if (L && !R) return L;
    if (!L && R) return R;
    return (
      <div className={`grid grid-cols-1 ${RATIO_COLS[ratio] || RATIO_COLS['50/50']} gap-4 items-stretch`}>
        {L}
        {R}
      </div>
    );
  }, [renderSlot]);

  const viewResolving = view.loadingClients || view.loadingProjects;
  const isLoading = viewResolving || intel.loading || (Boolean(clientId) && !payload && !intel.error);
  const isNoData = Boolean(payload) && (payload.status === 'no_data' || panels.length === 0);
  const noDataMessage = payload?.message || NO_DATA_COPY;
  const isHistorical = Boolean(payload?.is_historical);
  const HeaderComp = isClient ? ClientHeader : AdminHeader;

  const period = windowLabel(payloadWindow?.days_with_data);
  const hasTier1 = Boolean(payload?.verdict) || notable.length > 0;

  return (
    <>
        <main className="flex-1 min-w-0">
          <div className="search-console-page px-4 md:px-6 lg:px-10 py-6 space-y-6">
            {/* Header — title and context on the left, the admin-only as-of picker opposite
                on the right. The detail range picker lives lower, directly above the tables
                it governs (Tier 5), not here. The as-of picker selects which nightly
                snapshot to read; the page window is fixed at 90 days. Stacks on phones. */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Search Console</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  How people find your park on Google, and what they searched for.
                  {payload?.park && (
                    <span className="text-gray-400 dark:text-gray-500"> {payload.park}.</span>
                  )}
                </p>
              </div>
              {isAdmin && (
                <div className="shrink-0">
                  <AsOfPicker
                    snapshotDates={dates.data?.snapshot_dates || []}
                    value={asOf}
                    onChange={setAsOf}
                  />
                </div>
              )}
            </div>

            {isLoading ? (
              <SearchConsoleSkeleton />
            ) : !clientId ? (
              <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-4.35-4.35" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Search Console is not available for this property
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {clientDisplayName
                    ? `"${clientDisplayName}" does not have Search Console data.`
                    : 'The selected project does not have Search Console data.'}{' '}
                  Switch to another property in the header to load the Search Console dashboard.
                </p>
              </div>
            ) : intel.error ? (
              <ErrorState
                variant="card"
                title="Search Console"
                message={sanitizePanelError(intel.error)}
                onRetry={loadIntel}
              />
            ) : isNoData ? (
              <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-4.35-4.35" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {clientDisplayName
                    ? `Search Console is not ready for "${clientDisplayName}"`
                    : 'Search Console is not ready for this property'}
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{noDataMessage}</p>
              </div>
            ) : (
              <>
                {isHistorical && (
                  <HistoricalBanner asOfDate={payload.as_of_date} onReturnToToday={() => setAsOf(null)} />
                )}

                {/* TIER 1 — the verdict card (window label, freshness and the Pacific-time
                    note grouped under it) and the Worth Knowing chip band. Hidden entirely
                    when there is neither a verdict nor a notable panel (§4.5). */}
                {hasTier1 && (
                  <PanelErrorBoundary label="tier-1">
                    <div className="space-y-4">
                      <VerdictHeader
                        verdict={payload.verdict}
                        period={period}
                        asOfDate={payload.as_of_date}
                        computedAt={payload.computed_at}
                        isStale={payload.is_stale}
                        timezoneNote={payload.timezone_note}
                      />
                      <WorthKnowing panels={notable} onOpen={pushFrame} />
                    </div>
                  </PanelErrorBoundary>
                )}

                {/* TIER 2 — the demand KPI strip + trend (the money analogue). */}
                {byKey.search_headline && (
                  <PanelErrorBoundary label="tier-2">
                    {renderSlot('search_headline')}
                  </PanelErrorBoundary>
                )}

                {/* TIER 3 — the results ladder, the page's one face. Full-bleed with
                    whitespace reserved above and below (§4.9 / Part F). */}
                {byKey.position_bands && (
                  <PanelErrorBoundary label="tier-3">
                    <div className="py-2">{renderSlot('position_bands')}</div>
                  </PanelErrorBoundary>
                )}

                {/* TIER 4 — what to work on. The two row-heavy worklists run full width;
                    the rest are asymmetric pairs. */}
                <PanelErrorBoundary label="tier-4">
                  <div className="space-y-4">
                    {renderSlot('nearly_there')}
                    {renderSlot('new_queries')}
                    {TIER4_PAIRS.map((pair) => (
                      <React.Fragment key={pair.left}>{renderPair(pair)}</React.Fragment>
                    ))}
                  </div>
                </PanelErrorBoundary>

                {/* TIER 5 — the detail tables and secondary panels. The range control sits
                    directly above the tables it governs (the query/page tables + their
                    drills); the verdict and summary above are fixed to the nightly 90-day
                    snapshot. Compare (prior-period columns) shows in an expanded table. */}
                <PanelErrorBoundary label="tier-5">
                  <div className="space-y-4">
                    {dates.data?.detail_from && dates.data?.detail_to && (
                      <DetailRangePicker
                        from={detailRange.from}
                        to={detailRange.to}
                        minDate={dates.data.detail_from}
                        maxDate={dates.data.detail_to}
                        onChange={setDetailRange}
                        compare={compareRange}
                        onCompareChange={setCompareRange}
                      />
                    )}
                    {renderSlot('top_queries')}
                    {renderPair({ left: 'image_search', right: 'device_split', ratio: '50/50' })}
                    {renderSlot('top_pages')}
                    {renderSlot('query_page_map')}
                    {renderSlot('indexing_health')}
                    {renderSlot('sitemap_health')}
                    {renderPair({ left: 'discover_traffic', right: 'search_appearance', ratio: '50/50' })}
                    {/* §2.4.9 — any panel the tiers do not place (an unknown future
                        key) renders here rather than being dropped. */}
                    {panels
                      .filter((p) => p.panel && !PLACED_KEYS.has(p.panel))
                      .map((p) => renderPanel(p))}
                    {/* "How much history we hold" (data_coverage) is a reference footer —
                        it holds no daily figures to act on, so it sits last, after every
                        working panel. */}
                    {renderSlot('data_coverage')}
                  </div>
                </PanelErrorBoundary>
              </>
            )}
          </div>
        </main>

      {/* The one shared drill destination (§4.10). Renders only when the stack is
          non-empty; never navigates away. */}
      <DrillOver
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onRowDrill={onRowDrill}
        loading={Boolean(drillStack[drillStack.length - 1]?.loading)}
      />
    </>
  );
};

export default SearchConsolePage;
